import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Users, CreditCard, Star, TrendingUp, Loader2, ShieldAlert, Package, GraduationCap, Store, UserCog, Webhook, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchAdminResumen, fetchAdminPagos, fetchAdminSuscripciones, fetchAdminDestacados } from "../lib/admin";
import { apiAdminTareasPendientes } from "../lib/marketplaceApi";
import { PlanBadge } from "../components/PlanBadge";

const ROL_LABEL = { cliente: "Clientes", contratista: "Maestros-Contratistas", ferreteria: "Ferreterías", admin: "Administradores", sin_rol: "Sin rol" };

function StatCard({ Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid={`admin-stat-${label}`}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1a`, color }}>
          <Icon size={20} />
        </span>
        <div>
          <p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
          <p className="text-xs text-slate-500 mt-1">{label}</p>
        </div>
      </div>
    </div>
  );
}

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-PE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [resumen, setResumen] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [subs, setSubs] = useState([]);
  const [destacados, setDestacados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mktPend, setMktPend] = useState(0);

  useEffect(() => {
    Promise.all([fetchAdminResumen(), fetchAdminPagos(), fetchAdminSuscripciones(), fetchAdminDestacados()])
      .then(([r, p, s, d]) => { setResumen(r); setPagos(p); setSubs(s); setDestacados(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
    apiAdminTareasPendientes().then((t) => setMktPend(t?.total || 0)).catch(() => {});
  }, []);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <p className="mt-2 text-slate-500">Este panel es exclusivo para administradores.</p>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando panel…</div>;
  }

  const roles = resumen?.usuarios_por_rol || {};

  return (
    <div className="px-5 md:px-8 py-8" data-testid="admin-panel">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Ecosistema
      </Link>
      <header className="mt-4 mb-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">Panel de Administración</p>
        <h1 className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight">Control del Ecosistema</h1>
        <p className="mt-1 text-slate-500 text-sm">Métricas en tiempo real de la Familia ConstruLink.</p>
      </header>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Link to="/admin/software" data-testid="admin-link-software" className="flex items-center justify-between gap-3 rounded-2xl border border-brand-blue/20 bg-brand-blue/5 px-5 py-4 hover:bg-brand-blue/10 transition-colors">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center text-brand-blue"><Package size={20} /></span>
            <div>
              <p className="font-bold text-slate-900">Gestionar Pilar 02 · Software</p>
              <p className="text-xs text-slate-500">Sube y administra recursos digitales y programas APK</p>
            </div>
          </div>
          <span className="text-brand-blue font-semibold text-sm">Abrir →</span>
        </Link>

        <Link to="/admin/academia" data-testid="admin-link-academia" className="flex items-center justify-between gap-3 rounded-2xl border px-5 py-4 transition-colors" style={{ borderColor: "#EA580C33", backgroundColor: "#EA580C0d" }}>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#EA580C1a", color: "#EA580C" }}><GraduationCap size={20} /></span>
            <div>
              <p className="font-bold text-slate-900">Gestionar Pilar 03 · Academia</p>
              <p className="text-xs text-slate-500">Crea cursos, módulos y lecciones con video</p>
            </div>
          </div>
          <span className="font-semibold text-sm" style={{ color: "#EA580C" }}>Abrir →</span>
        </Link>

        <Link to="/admin/marketplace" data-testid="admin-link-marketplace" className="flex items-center justify-between gap-3 rounded-2xl border px-5 py-4 transition-colors" style={{ borderColor: "#D9770633", backgroundColor: "#D977060d" }}>
          <div className="flex items-center gap-3">
            <span className="relative w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#D977061a", color: "#D97706" }}>
              <Store size={20} />
              {mktPend > 0 && (
                <span data-testid="admin-marketplace-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[10px] font-bold flex items-center justify-center">
                  {mktPend > 99 ? "99+" : mktPend}
                </span>
              )}
            </span>
            <div>
              <p className="font-bold text-slate-900 flex items-center gap-2">Moderar Marketplace
                {mktPend > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-brand-red/10 text-brand-red">{mktPend} pendiente{mktPend > 1 ? "s" : ""}</span>}
              </p>
              <p className="text-xs text-slate-500">Revisa tiendas, productos y cursos de terceros</p>
            </div>
          </div>
          <span className="font-semibold text-sm" style={{ color: "#D97706" }}>Abrir →</span>
        </Link>

        <Link to="/admin/promociones" data-testid="admin-link-promociones" className="flex items-center justify-between gap-3 rounded-2xl border px-5 py-4 transition-colors" style={{ borderColor: "#B4530933", backgroundColor: "#B453090d" }}>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#B453091a", color: "#B45309" }}><Gift size={20} /></span>
            <div>
              <p className="font-bold text-slate-900">🎁 Beneficios Promocionales</p>
              <p className="text-xs text-slate-500">Membresías de cortesía y Programa Embajadores (sin pagos)</p>
            </div>
          </div>
          <span className="font-semibold text-sm" style={{ color: "#B45309" }}>Abrir →</span>
        </Link>
        <Link to="/admin/usuarios" data-testid="admin-link-usuarios" className="flex items-center justify-between gap-3 rounded-2xl border border-brand-red/20 bg-brand-red/5 px-5 py-4 hover:bg-brand-red/10 transition-colors">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center text-brand-red"><UserCog size={20} /></span>
            <div>
              <p className="font-bold text-slate-900">Gestión de Usuarios</p>
              <p className="text-xs text-slate-500">Suspender, anonimizar o eliminar cuentas (con auditoría)</p>
            </div>
          </div>
          <span className="text-brand-red font-semibold text-sm">Abrir →</span>
        </Link>

        <Link to="/admin/webhooks" data-testid="admin-link-webhooks" className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 px-5 py-4 hover:bg-emerald-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700"><Webhook size={20} /></span>
            <div>
              <p className="font-bold text-slate-900">Webhooks de Pago</p>
              <p className="text-xs text-slate-500">Historial de notificaciones de Mercado Pago (firma y procesamiento)</p>
            </div>
          </div>
          <span className="text-emerald-700 font-semibold text-sm">Abrir →</span>
        </Link>

        <Link to="/admin/marketplace/config-comercial" data-testid="admin-link-mkt-config" className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-4 hover:bg-amber-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700"><Store size={20} /></span>
            <div>
              <p className="font-bold text-slate-900">Marketplace · Configuración Comercial</p>
              <p className="text-xs text-slate-500">Comisiones por membresía y precio mínimo de venta</p>
            </div>
          </div>
          <span className="text-amber-700 font-semibold text-sm">Abrir →</span>
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard Icon={Users} label="Usuarios totales" value={resumen?.total_usuarios ?? 0} color="#2563EB" />
        <StatCard Icon={CreditCard} label="Suscriptores activos" value={resumen?.suscriptores_activos ?? 0} color="#7E22CE" />
        <StatCard Icon={Star} label="Destacados vigentes" value={resumen?.publicaciones_destacadas ?? 0} color="#D97706" />
        <StatCard Icon={TrendingUp} label="Ingresos (S/)" value={(resumen?.ingresos_totales ?? 0).toFixed(2)} color="#16A34A" />
      </div>

      {/* Usuarios por rol */}
      <section className="mt-8 bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Usuarios por rol</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(roles).map(([rol, total]) => (
            <div key={rol} className="rounded-xl border border-slate-100 p-4" data-testid={`admin-rol-${rol}`}>
              <p className="text-2xl font-extrabold text-slate-900">{total}</p>
              <p className="text-xs text-slate-500 mt-1">{ROL_LABEL[rol] || rol}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pagos recientes */}
        <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="admin-pagos">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Pagos recientes</h2>
          {pagos.length === 0 ? (
            <p className="text-sm text-slate-400">Sin pagos registrados aún.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-2">Usuario</th><th className="py-2 pr-2">Tipo</th><th className="py-2 pr-2">Monto</th><th className="py-2 pr-2">Estado</th><th className="py-2">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50">
                      <td className="py-2 pr-2 font-medium text-slate-700 truncate max-w-[120px]">{p.usuario}</td>
                      <td className="py-2 pr-2 text-slate-600">{p.tipo === "membresia" ? `Membresía ${p.plan}` : `Destacar ${p.dias}d`}</td>
                      <td className="py-2 pr-2 font-semibold">{p.gratis ? <span className="text-emerald-600">GRATIS</span> : `S/ ${Number(p.amount).toFixed(2)}`}</td>
                      <td className="py-2 pr-2">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{p.payment_status}</span>
                      </td>
                      <td className="py-2 text-slate-400 text-xs whitespace-nowrap">{fmtFecha(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Suscripciones activas */}
        <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="admin-suscripciones">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Suscripciones activas</h2>
          {subs.filter((s) => s.activa).length === 0 ? (
            <p className="text-sm text-slate-400">No hay suscripciones activas.</p>
          ) : (
            <ul className="space-y-2.5">
              {subs.filter((s) => s.activa).map((s) => (
                <li key={`${s.usuario}-${s.vigente_hasta}`} className="flex items-center justify-between gap-2 border-b border-slate-50 pb-2.5">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-700 truncate">{s.usuario}</p>
                    <p className="text-xs text-slate-400">Vence {fmtFecha(s.vigente_hasta)}</p>
                  </div>
                  <PlanBadge plan={s.plan} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Destacados vigentes */}
      <section className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="admin-destacados">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Publicaciones destacadas vigentes</h2>
        {destacados.length === 0 ? (
          <p className="text-sm text-slate-400">No hay publicaciones destacadas vigentes.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {destacados.map((d) => (
              <Link key={d.id} to={`/conexion/publicaciones/${d.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3 hover:bg-amber-50">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{d.titulo}</p>
                  <p className="text-xs text-slate-500">{d.usuario_nombre}</p>
                </div>
                <span className="text-xs text-amber-700 font-medium whitespace-nowrap inline-flex items-center gap-1"><Star size={12} /> {fmtFecha(d.destacado_hasta)}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
